import React from "react";
import { createBrowserRouter } from "react-router-dom";
import AdminLayout from "../layouts/Admin";
import ClientLayout from "../layouts/Client";
import ProfessionalLayout from "../layouts/Professional";
import ProtectedLayout from "../layouts/Protected";
import AdminProtectedLayout from "../layouts/Protected/Admin";
import RedirectIfLoggedIn from "../layouts/Redirect";
import AdminUsers from "../pages/Admin/AdminUsers";
import AdminCommunity from "../pages/Admin/Community";
import CommunitySubgroup from "../pages/Admin/CommunitySubgroup";
import AdminDashboard from "../pages/Admin/Dashboard";
import HMA from "../pages/Admin/HMA";
import HRV from "../pages/Admin/HRV";
import IodineTest from "../pages/Admin/IodineTest";
import IRCombi from "../pages/Admin/IRCombi";
import Professionals from "../pages/Admin/Professionals";
import Tags from "../pages/Admin/Tags";
import Terms from "../pages/Admin/Terms";
import AdminLogin from "../pages/Auth/AdminLogin";
import Login from "../pages/Auth/Login";
import ClientInfo from "../pages/Client/ClientInfo";
import ClientDashboard from "../pages/Client/Dashboard";
import Questionnaires from "../pages/Client/Questionnaires";
import InitialQuestionnaireLayout from "../pages/Client/Questionnaires/QuestionnaireLayout";
import ActionPlanStatus from "../pages/Professional/ActionPlanStatus";
import HMAResult from "../pages/Professional/Analyses/HMA/Result";
import HRVResult from "../pages/Professional/Analyses/HRV/Result";
import HRVShowResult from "../pages/Professional/Analyses/HRV/Show";
import IodineTestResult from "../pages/Professional/Analyses/IodineTest/Result";
import IRCombiResult from "../pages/Professional/Analyses/IRCombi/Result";
import ProfessionalClientView from "../pages/Professional/Client";
import InitialClientLayout from "../pages/Professional/Client/InitialClientLayout";
import ProfessionalCommunity from "../pages/Professional/Community";
import InitialCommunityLayout from "../pages/Professional/Community/InitialCommunityLayout";
import CommunityForumView from "../pages/Professional/Community/View";
import ProfessionalDashboard from "../pages/Professional/Dashboard";
import EditProfile from "../pages/Professional/EditProfile";
import InMail from "../pages/Professional/InMail";
import QuestionnaireResponse from "../pages/Professional/QuestionnaireResponse";
import QuestionnairePreview from "../pages/Professional/Questionnaires/Preview";
import TwoFactorAuthentication from "../pages/Professional/TwoFactorAuthentication";
import LibraryOfTerms from "../pages/public/LibraryOfTerms";

const NotFound = () => {
  React.useEffect(() => {
    window.location.reload();
  }, []);

  return null; // This prevents rendering anything while the page is refreshing
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RedirectIfLoggedIn element={<Login />} />,
  },
  {
    path: "/signin",
    element: <RedirectIfLoggedIn element={<Login />} />,
  },
  {
    path: "/a/sessions",
    element: <RedirectIfLoggedIn element={<AdminLogin />} />,
  },
  {
    // ADMIN ROUTES
    path: "/a",
    element: <AdminProtectedLayout layout={<AdminLayout />} />,
    children: [
      {
        path: "/a",
        element: <AdminDashboard />,
      },
      {
        path: "account/edit",
        element: <EditProfile />,
      },
      {
        path: "admins",
        element: <AdminUsers />,
      },
      {
        path: "professionals",
        element: <Professionals />,
      },
      {
        path: "hma_imports",
        element: <HMA />,
      },
      {
        path: "hrv_imports",
        element: <HRV />,
      },
      {
        path: "ir_combis",
        element: <IRCombi />,
      },
      {
        path: "iodine_tests",
        element: <IodineTest />,
      },
      {
        path: "terms",
        element: <Terms />,
      },
      {
        path: "tags",
        element: <Tags />,
      },
      {
        path: "forums",
        element: <AdminCommunity />,
      },
      {
        path: "community_subgroups",
        element: <CommunitySubgroup />,
      },
    ],
  },
  {
    // CLIENT ROUTES
    path: "/c/:code",
    element: <ProtectedLayout layout={<ClientLayout />} />,
    children: [
      {
        path: "",
        element: <ClientDashboard />,
      },
      {
        path: "general_client_info",
        element: <ClientInfo />,
      },
      {
        path: "account/edit",
        element: <EditProfile />,
      },
      {
        path: "questionnaires",
        element: <InitialQuestionnaireLayout />,
        children: [
          {
            path: ":questionnaireId/new",
            element: <Questionnaires />,
          },
        ],
      },
    ],
  },
  {
    // PROFESSIONAL ROUTES
    path: "/p/:code",
    element: <ProtectedLayout layout={<ProfessionalLayout />} />,
    children: [
      {
        path: "",
        element: <ProfessionalDashboard />,
      },
      {
        path: "action_plan_statuses",
        element: <ActionPlanStatus />,
      },
      {
        path: "account/edit",
        element: <EditProfile />,
      },
      {
        path: "clients",
        element: <InitialClientLayout />,
        children: [
          {
            path: ":clientId",
            element: <ProfessionalClientView />,
          },
          {
            path: ":clientId/general_client_info",
            element: <ClientInfo />,
          },
        ],
      },
      {
        path: "questionnaire_responses/:id",
        element: <QuestionnaireResponse />,
      },
      {
        path: "ir_combis/:irCombiId",
        element: <IRCombiResult />,
      },
      {
        path: "hrv_reports/:hrvId",
        element: <HRVResult />,
      },
      {
        path: "hrv_reports/:hrvId/show_data",
        element: <HRVShowResult />,
      },
      {
        path: "hma_results/:hmaId",
        element: <HMAResult />,
      },
      {
        path: "iodine_tests/:iodineTestId",
        element: <IodineTestResult />,
      },
      {
        path: "questionnaire_preview/:questionnaireType",
        element: <QuestionnairePreview />,
      },
      {
        path: "terms/:slug",
        element: <LibraryOfTerms />,
      },
      {
        path: "client_messages",
        element: <InMail />,
        children: [
          {
            path: ":clientId",
            element: <InMail />,
          },
        ],
      },
      {
        path: "two_factor_authentication/edit",
        element: <TwoFactorAuthentication />,
      },
      {
        path: "forums",
        element: <InitialCommunityLayout />,
        children: [
          {
            path: "",
            element: <ProfessionalCommunity />,
          },
          {
            path: ":forumId/forum_topics",
            element: <CommunityForumView />,
          },
        ],
      },
    ],
  },
  // {
  //   path: '/account/edit',
  //   element: <EditProfile />
  // },
  // Global 404 route (required)
  {
    path: "*",
    element: <NotFound />,
  },
]);
